// Awards (AWARDS.md): the week's awards as cards with a period stepper,
// the season race or its results with the champion on top, and the trophy
// rooms. Every card opens a detail with the top five and Share; every team
// opens their room.

import { useState } from 'react';
import type { AwardCardView, AwardsView, TeamRef } from '@fsp/domain';
import { useSearchParams } from 'react-router-dom';
import { useAwardsView, useLeagueBase, useMyTeamId } from '../data/hooks';
import { EmptyState, LiveDot, TeamMark } from '../kit';
import { Button, Caption, Cover, Segments, Text, Title } from '../kit/ui';
import { WeekStepper } from './shared/WeekStepper';
import '../styles/screens/waivers.css';
import '../styles/screens/league-screens.css';
import { useTier } from '../shell/useTier';
import { AwardCard, AwardDetail, AwardIcon, Cup, TIER_LABEL } from './shared/AwardKit';
import { FromLeague } from './shared/FromLeague';
import { TeamName } from './shared/Names';
import { TrophyRoom } from './shared/TrophyRoom';

type Tab = 'week' | 'season' | 'room';

function tabOf(params: URLSearchParams): Tab {
  return params.get('tab') === 'season' ? 'season' : params.get('tab') === 'room' ? 'room' : 'week';
}

function periodOf(params: URLSearchParams): number | undefined {
  const raw = params.get('period');
  return raw && /^\d+$/.test(raw) ? Number(raw) : undefined;
}

export function AwardsScreen() {
  const [params] = useSearchParams();
  const tab = tabOf(params);
  // A real league's awards are a read (PLAN-2026-09-26-season-end S4): not
  // yet, here, or the server's own sentence, as every league screen has them.
  return (
    <FromLeague snapshot={useAwardsView(tab === 'week' ? periodOf(params) : undefined)}>
      {(awards) => <AwardsBody awards={awards} />}
    </FromLeague>
  );
}

function AwardsBody({ awards }: { awards: AwardsView }) {
  const [params, setParams] = useSearchParams();
  const tab = tabOf(params);
  const period = periodOf(params);
  const myTeamId = useMyTeamId();
  const base = useLeagueBase();
  const teamParam = params.get('team') ?? myTeamId;
  const tier = useTier();
  const phone = tier === 'phone';
  const columns = phone ? 1 : tier === 'tablet' ? 2 : 3;
  const [open, setOpen] = useState<AwardCardView | null>(null);

  const go = (next: Partial<{ tab: Tab; period: number; team: string }>) => {
    const merged = {
      tab: next.tab ?? tab,
      period: next.period ?? period,
      team: next.team ?? params.get('team'),
    };
    const out: Record<string, string> = {};
    if (merged.tab !== 'week') out.tab = merged.tab;
    if (merged.tab === 'week' && merged.period) out.period = String(merged.period);
    if (merged.tab === 'room' && merged.team) out.team = merged.team;
    setParams(out, { replace: true });
  };
  const toRoom = (team: TeamRef) => {
    setOpen(null);
    go({ tab: 'room', team: team.id });
  };

  const header = (
    <div className="fsp-waivers-head">
      <div className="fsp-waivers-head-body">
        <Title>
          {tab === 'week' ? 'Awards' : tab === 'season' ? 'Season awards' : 'Trophy room'}
        </Title>
        <div className="fsp-sub fsp-ui-wrap-text">
          {tab === 'week'
            ? 'Handed out once each week’s scores are final. The whole league sees them; that is the point.'
            : tab === 'season'
              ? awards.season.status === 'final'
                ? `The ${awards.league.season} season, decided.`
                : 'The race, as it stands. Decided when the championship is.'
              : 'Every season, every trophy, every milestone. Visit anyone.'}
        </div>
      </div>
      {tab === 'season' && !phone ? (
        <Button to={`${base}/history`} size="xs" variant="quiet">
          League history
        </Button>
      ) : null}
      <span className={phone ? 'fsp-grow' : 'fsp-awards-tabs'}>
        <Segments
          label="Which awards"
          items={[
            { id: 'week', label: 'Week' },
            { id: 'season', label: 'Season' },
            { id: 'room', label: 'Trophy room' },
          ]}
          value={tab}
          onChange={(id) => go({ tab: id })}
        />
      </span>
    </div>
  );

  const stepper = (
    <div className="fsp-waivers-head fsp-awards-step-head">
      {/* A WEEK PICKED AS WELL AS STEPPED (B-418). */}
      <WeekStepper
        periods={awards.periods}
        current={awards.period.index}
        label={awards.period.label}
        onGo={(p) => go({ period: p })}
      />
      <span className="fsp-sub fsp-awards-state">
        {awards.status === 'live' ? (
          <>
            <LiveDot />
            As it stands
          </>
        ) : awards.status === 'final' ? (
          `${awards.perPeriod.length} award${awards.perPeriod.length === 1 ? '' : 's'}`
        ) : (
          'Nothing yet'
        )}
      </span>
    </div>
  );

  return (
    <div className="fsp-screen fsp-flow">
      <div className="fsp-pinned">{header}</div>
      {tab === 'week' ? <div className="fsp-pinned fsp-pinned-stick">{stepper}</div> : null}
      <div className="fsp-regions fsp-board-one">
        <section className="fsp-region fsp-region-plain" aria-label={tab}>
          {tab === 'week' ? (
            awards.perPeriod.length === 0 ? (
              <div className="fsp-empty">
                {awards.status === 'final'
                  ? 'Too few games this week for weekly awards. The season awards are the story.'
                  : 'Awards are handed out once the week’s scores are final. Nothing yet.'}
              </div>
            ) : (
              <div className={`fsp-awards-grid fsp-widgets-${columns}`}>
                {awards.perPeriod.map((c) => (
                  <AwardCard key={c.id} card={c} onOpen={setOpen} />
                ))}
              </div>
            )
          ) : tab === 'season' ? (
            <>
              {awards.season.champion ? (
                <div className="fsp-awards-champion">
                  <div className="fsp-surface fsp-champion-banner fsp-row-clickable fsp-cover-host">
                    {/* The banner opens the champion's trophy room; the name
                        opens the team (B-220). */}
                    <Cover
                      label={`${awards.season.champion.team.name}'s trophy room`}
                      onClick={() => toRoom(awards.season.champion!.team)}
                    />
                    <Cup
                      primary={awards.season.champion.team.colors.primary}
                      secondary={awards.season.champion.team.colors.secondary}
                      size={64}
                    />
                    <div className="fsp-champion-banner-body">
                      <div className="fsp-cap fsp-awards-glory" data-tone="glory">
                        {awards.season.champion.season} {awards.season.champion.trophyName}
                      </div>
                      <div className="fsp-disp fsp-awards-champion-name">
                        <TeamName team={awards.season.champion.team} />
                      </div>
                      <div className="fsp-sub fsp-ui-wrap-text">{awards.season.champion.line}</div>
                    </div>
                    <TeamMark team={awards.season.champion.team} size={36} />
                  </div>
                </div>
              ) : null}
              <div className={`fsp-awards-grid fsp-widgets-${columns}`}>
                {awards.season.awards
                  .filter((c) => c.awardId !== 'champion' || !awards.season.champion)
                  .map((c) => (
                    <AwardCard key={c.id} card={c} onOpen={setOpen} />
                  ))}
              </div>
              <div className="fsp-region-head fsp-region-head-static">
                <Caption>Recent milestones</Caption>
              </div>
              {awards.recentUnlocks.length === 0 ? (
                <div className="fsp-awards-note">
                  <Text tone="secondary">
                    Nobody has reached one yet. The first win is the first milestone.
                  </Text>
                </div>
              ) : (
                awards.recentUnlocks.map((u) => (
                  <div
                    key={u.id}
                    className="fsp-row fsp-row-clickable fsp-awards-unlock fsp-cover-host"
                  >
                    <Cover label={`${u.team.name}'s trophy room`} onClick={() => toRoom(u.team)} />
                    <span className="fsp-badge-glyph fsp-chat-card-badge" data-tier={u.tier}>
                      <AwardIcon icon={u.icon} size={16} />
                    </span>
                    <div className="fsp-waivers-who">
                      <div className="fsp-name">
                        <span className="fsp-name-text fsp-awards-unlock-name">
                          <TeamName team={u.team}>{u.team.ownerName}</TeamName> unlocked {u.name}
                        </span>
                        <span className="fsp-badge-tier" data-tier={u.tier}>
                          {TIER_LABEL[u.tier]}
                        </span>
                      </div>
                      <div className="fsp-sub fsp-ui-wrap-text">
                        {u.earnedIn ? `${u.earnedIn} · ${u.line}` : u.line}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </>
          ) : // No team means no room to show — a real league whose home view
          // has not landed, or one somebody has no seat in (E12).
          teamParam === null ? (
            <EmptyState>No team to show a trophy room for.</EmptyState>
          ) : (
            <TrophyRoom teamId={teamParam} onPick={(id) => go({ tab: 'room', team: id })} />
          )}
        </section>
      </div>
      <AwardDetail
        card={open}
        league={{ name: awards.league.name, season: awards.league.season }}
        onClose={() => setOpen(null)}
        onTeam={toRoom}
      />
    </div>
  );
}
